import React, { ReactNode } from 'react'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { DomainDto } from '../../dtos/domain'
import { FEAT_DOMAIN_INTEGRATIONS } from '../../constants'
import { isDeliveryChannelEnabled } from '../../_utils/domain'
import DomainDataForm from '../../components/domain-data-form/domain-data-form'
import { UserList } from '../../components/user-list/user-list'
import { DomainIntegrationsManager } from '../../components/domain-integrations-manager/domain-integrations-manager'
import DomainNotificationSettings from '../../components/domain-notification-settings/domain-notification-settings'
import DomainPromptSettings from '../../components/domain-prompt-settings/domain-prompt-settings'
import DomainAiSettings from '../../components/domain-ai-settings/domain-ai-settings'
import { AI_VIEW_LABELS, AiView, domainHasFlag } from '../../components/domain-ai-settings/constants'
import DomainSegmentationSettings from '../../components/domain-segmentation-settings/domain-segmentation-settings'
import { KeywordManagerV2 } from '../../components/keyword-manager/keyword-manager-v2'
import { ApiTokensDataView } from '../../components/api-tokens/api-tokens-data-view'
import { DomainSecretsView } from '../../components/domain-secrets-view/domain-secrets-view'
import { DomainEventWebhooksTab } from '../../components/domain-event-webhooks/domain-event-webhooks-tab'

export type SettingsGroup = 'domain' | 'engage' | 'audience' | 'ai' | 'developers'

export const DEFAULT_SETTINGS_KEY = 'settings'
export const DEFAULT_AI_KEY = `ai/${AiView.OVERVIEW}`

export interface ISettingsGroup {
    key: string
    label: string
}

export const SETTINGS_GROUPS: ISettingsGroup[] = [
    { key: 'domain', label: 'Domain' },
    { key: 'engage', label: 'Messaging' },
    { key: 'audience', label: 'Audience' },
    { key: 'ai', label: 'Pushly AI' },
    { key: 'developers', label: 'Developers' },
]

export interface ISettingsContext {
    domainId: number
    domain?: DomainDto
    currentUser?: { isInternalUser?: boolean } | null
    /** Routes to an AI view; the AI overview links to the other views through it. */
    onAiViewChange?: (view: AiView) => void
}

export type SettingsVisibility = boolean | 'loading'

/** A page of a settings layout. `C` is what its context carries: the domain's by default, the organization's elsewhere. */
export interface ISettingsPage<C = ISettingsContext> {
    /** Hash key, unchanged from the old tab strip. AI views are `ai/<view>`. */
    key: string
    group: string
    label: string
    /** `'loading'` while the flags it depends on are not yet known. */
    visible: (ctx: C) => SettingsVisibility
    render: (ctx: C) => ReactNode
    /**
     * Pages with the same mount key share one mounted subtree, so moving between them updates it in place
     * rather than remounting it. The AI views share one, so switching views does not refetch the domain.
     */
    mountKey?: string
    /** Not rendered in NS-3108 (copy TBD). */
    description?: string
    /** Reserved for "New" chips; unused. */
    tag?: string
    /** Reserved for status dots; unused. */
    status?: (ctx: C) => unknown
}

const always = () => true

/**
 * Held back until the Curator and Page Analysis release. The pages stay registered so links to them fall back to
 * the AI overview rather than to the domain overview.
 */
const unreleased = () => false

/** Domain flags are absent until the domain has resolved. */
const flagGate =
    (flag: string) =>
    (ctx: ISettingsContext): SettingsVisibility =>
        ctx.domain ? domainHasFlag(ctx.domain, flag) : 'loading'

const aiPage = (view: AiView, visible: ISettingsPage['visible']): ISettingsPage => ({
    key: `ai/${view}`,
    group: 'ai',
    label: AI_VIEW_LABELS[view],
    visible,
    mountKey: 'ai',
    render: (ctx) => (
        <DomainAiSettings
            key={`dai-${ctx.domainId}`}
            domainId={ctx.domainId}
            view={view}
            availableViews={getAiViews(ctx)}
            onViewChange={ctx.onAiViewChange ?? (() => undefined)}
        />
    ),
})

export const SETTINGS_PAGES: ISettingsPage[] = [
    {
        key: 'settings',
        group: 'domain',
        label: 'Overview',
        visible: always,
        render: (ctx) => <DomainDataForm key={`ddf-${ctx.domainId}`} domainId={ctx.domainId} />,
    },
    {
        key: 'users',
        group: 'domain',
        label: 'Users',
        visible: always,
        render: (ctx) => <UserList key={`dul-${ctx.domainId}`} options={{ domainId: ctx.domainId }} />,
    },
    {
        key: 'integrations',
        group: 'domain',
        label: 'Integrations',
        visible: flagGate(FEAT_DOMAIN_INTEGRATIONS),
        render: (ctx) => <DomainIntegrationsManager key={`dim-${ctx.domainId}`} />,
    },
    {
        key: 'security',
        group: 'domain',
        label: 'Security',
        visible: always,
        render: (ctx) => <DomainSecretsView id={ctx.domainId} />,
    },
    {
        key: 'notifications',
        group: 'engage',
        label: 'Notifications',
        visible: always,
        render: (ctx) => <DomainNotificationSettings key={`ddf-${ctx.domainId}`} domainId={ctx.domainId} />,
    },
    {
        key: 'keywords',
        group: 'audience',
        label: 'Keywords',
        visible: always,
        render: (ctx) => (
            <KeywordManagerV2
                level="domain"
                domainId={ctx.domainId}
                actions={{ actions: { addKeywords: true, bulkKeywordActions: true, exportKeywords: true } }}
            />
        ),
    },
    {
        key: 'prompts',
        group: 'audience',
        label: 'Prompts',
        visible: (ctx) => (ctx.domain ? isDeliveryChannelEnabled(ctx.domain, DeliveryChannel.WEB) : 'loading'),
        render: (ctx) => <DomainPromptSettings key={`ddf-${ctx.domainId}`} domainId={ctx.domainId} />,
    },
    {
        key: 'segmentation',
        group: 'audience',
        label: 'Segmentation',
        visible: always,
        render: (ctx) => <DomainSegmentationSettings key={`ddf-${ctx.domainId}`} domainId={ctx.domainId} />,
    },
    aiPage(AiView.OVERVIEW, always),
    // Always in the rail: without Pushly AI the page explains what Brand Voice is and what it needs.
    aiPage(AiView.BRAND_VOICE, always),
    aiPage(AiView.CURATOR, unreleased),
    aiPage(AiView.PAGE_ANALYSIS, unreleased),
    {
        key: 'api-tokens',
        group: 'developers',
        label: 'API Tokens',
        visible: always,
        render: (ctx) => <ApiTokensDataView level="domain" id={ctx.domainId} />,
    },
    {
        key: 'event-webhooks',
        group: 'developers',
        label: 'Event Webhooks',
        visible: always,
        render: (ctx) => <DomainEventWebhooksTab key={`dewt-${ctx.domainId}`} domainId={ctx.domainId} />,
    },
]

/**
 * Pages in registry order that are shown in the rail. A page still loading
 * is not listed until it resolves.
 */
export const getVisiblePages = <C = ISettingsContext,>(
    ctx: C,
    pages: ISettingsPage<C>[] = SETTINGS_PAGES as unknown as ISettingsPage<C>[],
): ISettingsPage<C>[] => pages.filter((page) => page.visible(ctx) === true)

/**
 * The AI views the rail lists, for the AI section's own view switcher, so the two never disagree. `'loading'`
 * while any of their flags is unknown.
 */
export const getAiViews = (ctx: ISettingsContext): AiView[] | 'loading' => {
    const aiPages = SETTINGS_PAGES.filter((page) => page.group === 'ai')
    if (aiPages.some((page) => page.visible(ctx) === 'loading')) return 'loading'
    return aiPages.filter((page) => page.visible(ctx) === true).map((page) => page.key.slice('ai/'.length) as AiView)
}

/** A group with the pages the user can see in it, as the navigation shows it. */
export interface ISettingsNavGroup<C = ISettingsContext> extends ISettingsGroup {
    pages: ISettingsPage<C>[]
}

export interface IResolvedPage<C = ISettingsContext> {
    page: ISettingsPage<C>
    /** True while the requested page's flags are unknown; render its loading state. */
    loading: boolean
}

/**
 * Hash to page. `#ai` and `#ai/<unknown>` mean the AI overview; an unknown or
 * hidden key falls back to Overview. While a requested page is `'loading'` it
 * is returned as-is so a deep link is not bounced early.
 */
export const resolveSettingsPage = <C = ISettingsContext,>(
    hash: string,
    ctx: C,
    pages: ISettingsPage<C>[] = SETTINGS_PAGES as unknown as ISettingsPage<C>[],
): IResolvedPage<C> => {
    const segments = (hash || '').replace('#', '').toLowerCase().split('/').filter(Boolean)
    const find = (key: string) => pages.find((p) => p.key === key)
    const fallback = find(DEFAULT_SETTINGS_KEY)!

    let requested: ISettingsPage<C> | undefined
    if (segments[0] === 'ai') {
        requested = find(`ai/${segments[1]}`) ?? find(DEFAULT_AI_KEY)
        // An AI view the user cannot reach falls back to the AI overview.
        if (requested && requested.visible(ctx) === false) requested = find(DEFAULT_AI_KEY)
    } else if (segments[0]) {
        requested = find(segments[0])
    } else {
        requested = fallback
    }

    if (!requested) return { page: fallback, loading: false }

    const visible = requested.visible(ctx)
    if (visible === 'loading') return { page: requested, loading: true }
    if (visible === false) return { page: fallback, loading: false }

    return { page: requested, loading: false }
}
